import { z } from 'zod';

/**
 * プロジェクト作成・更新用のバリデーションスキーマ
 */

// 基本プロジェクトバリデーション
export const projectBaseSchema = z.object({
    name: z.string()
        .min(1, 'プロジェクト名は必須です')
        .max(100, 'プロジェクト名は100文字以内で入力してください')
        .regex(/^[^\s].*[^\s]$|^[^\s]$/, 'プロジェクト名の前後に空白文字は使用できません'),

    address: z.string()
        .min(1, '住所は必須です')
        .max(200, '住所は200文字以内で入力してください'),

    building_type: z.string()
        .optional(),

    start_date: z.string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, '開始日はYYYY-MM-DD形式で入力してください')
        .optional()
        .or(z.literal('')),

    end_date: z.string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, '終了日はYYYY-MM-DD形式で入力してください')
        .optional()
        .or(z.literal('')),

    notes: z.string()
        .max(1000, '備考は1000文字以内で入力してください')
        .optional()
        .or(z.literal('')),

    status: z.string()
        .optional(),
});

// プロジェクト作成用スキーマ
export const projectCreateSchema = projectBaseSchema;

// プロジェクト更新用スキーマ
export const projectUpdateSchema = projectBaseSchema.partial();

// 工区バリデーションスキーマ
export const projectZoneSchema = z.object({
    name: z.string()
        .min(1, '工区名は必須です')
        .max(50, '工区名は50文字以内で入力してください')
        .regex(/^[^\s].*[^\s]$|^[^\s]$/, '工区名の前後に空白文字は使用できません'),

    area_sqm: z.number()
        .min(0, '面積は0以上である必要があります')
        .max(10000, '面積は10,000㎡以下である必要があります')
        .optional(),

    notes: z.string()
        .max(500, '備考は500文字以内で入力してください')
        .optional()
        .or(z.literal('')),
});

// 工区更新用スキーマ
export const projectZoneUpdateSchema = z.object({
    name: z.string()
        .min(1, '工区名は必須です')
        .max(50, '工区名は50文字以内で入力してください')
        .regex(/^[^\s].*[^\s]$|^[^\s]$/, '工区名の前後に空白文字は使用できません')
        .optional(),

    area_sqm: z.number()
        .min(0, '面積は0以上である必要があります')
        .max(10000, '面積は10,000㎡以下である必要があります')
        .optional(),

    notes: z.string()
        .max(500, '備考は500文字以内で入力してください')
        .optional()
        .or(z.literal('')),
});

// カスタムバリデーション関数
export const validateProjectDates = (startDate?: string, endDate?: string) => {
    if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (start > end) {
            return {
                success: false,
                error: '開始日は終了日より前である必要があります'
            };
        }
    }

    return { success: true };
};

export const validateProjectName = (name: string) => {
    // 禁止文字のチェック
    const forbiddenChars = /[<>:"/\\|?*]/;
    if (forbiddenChars.test(name)) {
        return {
            success: false,
            error: 'プロジェクト名に使用できない文字が含まれています'
        };
    }

    // 連続する空白のチェック
    if (/\s{2,}/.test(name)) {
        return {
            success: false,
            error: 'プロジェクト名に連続する空白文字は使用できません'
        };
    }

    return { success: true };
};

export const validateProjectZoneName = (name: string, existingZones: string[]) => {
    // 重複チェック
    if (existingZones.includes(name)) {
        return {
            success: false,
            error: '同じ名前の工区が既に存在します'
        };
    }

    return { success: true };
};

// 型定義
export type ProjectCreateInput = z.infer<typeof projectCreateSchema>;
export type ProjectUpdateInput = z.infer<typeof projectUpdateSchema>;
export type ProjectZoneInput = z.infer<typeof projectZoneSchema>;
export type ProjectZoneUpdateInput = z.infer<typeof projectZoneUpdateSchema>;

// バリデーション結果型
export interface ValidationResult {
    success: boolean;
    error?: string;
    errors?: Record<string, string[]>;
}

/**
 * プロジェクト作成データのバリデーション
 */
export function validateProjectCreate(data: unknown): ValidationResult {
    try {
        projectCreateSchema.parse(data);
        return { success: true };
    } catch (error) {
        if (error instanceof z.ZodError) {
            const errors: Record<string, string[]> = {};
            if (error.issues && Array.isArray(error.issues)) {
                error.issues.forEach((issue) => {
                    const path = issue.path.join('.');
                    if (!errors[path]) {
                        errors[path] = [];
                    }
                    errors[path].push(issue.message);
                });
            }
            return { success: false, errors };
        }
        return { success: false, error: 'バリデーションエラーが発生しました' };
    }
}

/**
 * プロジェクト更新データのバリデーション
 */
export function validateProjectUpdate(data: unknown): ValidationResult {
    try {
        projectUpdateSchema.parse(data);
        return { success: true };
    } catch (error) {
        if (error instanceof z.ZodError) {
            const errors: Record<string, string[]> = {};
            if (error.issues && Array.isArray(error.issues)) {
                error.issues.forEach((issue) => {
                    const path = issue.path.join('.');
                    if (!errors[path]) {
                        errors[path] = [];
                    }
                    errors[path].push(issue.message);
                });
            }
            return { success: false, errors };
        }
        return { success: false, error: 'バリデーションエラーが発生しました' };
    }
}

/**
 * 工区データのバリデーション
 */
export function validateProjectZone(data: unknown): ValidationResult {
    try {
        projectZoneSchema.parse(data);
        return { success: true };
    } catch (error) {
        if (error instanceof z.ZodError) {
            const errors: Record<string, string[]> = {};
            if (error.issues && Array.isArray(error.issues)) {
                error.issues.forEach((issue) => {
                    const path = issue.path.join('.');
                    if (!errors[path]) {
                        errors[path] = [];
                    }
                    errors[path].push(issue.message);
                });
            }
            return { success: false, errors };
        }
        return { success: false, error: 'バリデーションエラーが発生しました' };
    }
}

/**
 * 工区更新データのバリデーション
 */
export function validateProjectZoneUpdate(data: unknown): ValidationResult {
    try {
        projectZoneUpdateSchema.parse(data);
        return { success: true };
    } catch (error) {
        if (error instanceof z.ZodError) {
            const errors: Record<string, string[]> = {};
            if (error.issues && Array.isArray(error.issues)) {
                error.issues.forEach((issue) => {
                    const path = issue.path.join('.');
                    if (!errors[path]) {
                        errors[path] = [];
                    }
                    errors[path].push(issue.message);
                });
            }
            return { success: false, errors };
        }
        return { success: false, error: 'バリデーションエラーが発生しました' };
    }
}
